/**
 * Access Request Modal
 *
 * UX15: Enhanced with immediate validation feedback for better UX.
 * Modal for requesting access to gated content with real-time validation.
 *
 * Validation Strategy:
 * - Validates on blur (after user leaves field)
 * - After first blur, validates on change (immediate feedback)
 * - Clear visual feedback (success/error states)
 *
 * Security: All personal data is transmitted securely and never exposed
 * in public APIs or client-side state beyond form submission.
 */

import { useSubmitAccessRequest } from '@/hooks/api/useAccessControl'
import type { AccessRequestInput } from '@/hooks/api/useAccessControl'
import { useFormValidation } from '@/hooks/useFormValidation'
import { Input } from '@/components/ui/Input'
import {
  validateRequired,
  validateEmail,
  validateURL,
  composeValidators,
  createFormValidator,
} from '@/lib/validation'

interface AccessRequestModalProps {
  isOpen: boolean
  onClose: () => void
  requestedContent: string[] // e.g., ['background', 'interests']
  contentLabel?: string // e.g., "Full Background"
}

export function AccessRequestModal({
  isOpen,
  onClose,
  requestedContent,
  contentLabel = 'detailed content',
}: AccessRequestModalProps) {
  const submitMutation = useSubmitAccessRequest()

  // UX15: Immediate validation with smart timing
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    reset,
  } = useFormValidation<AccessRequestInput>({
    initialValues: {
      email: '',
      name: '',
      company: '',
      linkedIn: '',
      reason: '',
      contentTypes: requestedContent,
    },
    validate: createFormValidator<AccessRequestInput>({
      email: composeValidators(
        (v) => validateRequired(v, 'Email'),
        (v) => validateEmail(v)
      ),
      name: (v) => validateRequired(v, 'Full Name'),
      linkedIn: (v) => (v ? validateURL(v) : ''), // Optional but must be valid if provided
    }),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields before submission
    const isValid = await validateForm()
    if (!isValid) {
      return
    }

    try {
      const result = await submitMutation.mutateAsync(values)

      if (result.success) {
        // Show success message
        alert(
          '✅ Access request submitted!\n\nYour request has been received and will be reviewed. If approved, you will receive an email with access instructions.'
        )
        reset()
        onClose()
      } else {
        alert(`❌ Error: ${result.error || 'Failed to submit request'}`)
      }
    } catch (error) {
      console.error('Error submitting access request:', error)
      alert('❌ An error occurred. Please try again.')
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--spacing-md)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '500px',
          width: '100%',
          padding: 'var(--spacing-2xl)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--spacing-sm)',
            }}
          >
            🔒 Request Access
          </h2>
          <p
            style={{
              fontSize: '0.9375rem',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.7,
            }}
          >
            Request access to {contentLabel}. Your request will be reviewed and you'll
            receive an email if approved.
          </p>
        </div>

        {/* Form with UX15 Immediate Validation */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <Input
            label="Email"
            name="email"
            type="email"
            required
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.email ? errors.email : undefined}
            placeholder="your.email@company.com"
            fullWidth
          />

          {/* Name */}
          <Input
            label="Full Name"
            name="name"
            type="text"
            required
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name ? errors.name : undefined}
            placeholder="Jane Doe"
            fullWidth
          />

          {/* Company */}
          <Input
            label="Company"
            name="company"
            type="text"
            value={values.company}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Acme Corp"
            helperText="Optional"
            fullWidth
          />

          {/* LinkedIn */}
          <Input
            label="LinkedIn Profile"
            name="linkedIn"
            type="url"
            value={values.linkedIn}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.linkedIn ? errors.linkedIn : undefined}
            placeholder="https://linkedin.com/in/yourprofile"
            helperText="Optional but recommended"
            fullWidth
          />

          {/* Reason */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <label
              htmlFor="reason"
              className="input__label"
              style={{ marginBottom: 'var(--spacing-xs)', display: 'block' }}
            >
              Why are you interested?
            </label>
            <textarea
              id="reason"
              name="reason"
              className="input"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="I'm interested in learning more about your work in..."
              rows={3}
              style={{ resize: 'vertical' }}
            />
            <p className="input__helper" style={{ marginTop: 'var(--spacing-xs)' }}>
              Optional
            </p>
          </div>

          {/* Privacy Notice */}
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-lg)',
              padding: 'var(--spacing-sm)',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            🔒 <strong>Privacy:</strong> Your information will only be used to evaluate
            this access request and will not be shared with third parties.
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <button
              type="submit"
              disabled={submitMutation.isPending}
              style={{
                flex: 1,
                padding: 'var(--spacing-md)',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: submitMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: submitMutation.isPending ? 0.6 : 1,
              }}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
            <button
              type="button"
              onClick={() => {
                reset()
                onClose()
              }}
              disabled={submitMutation.isPending}
              style={{
                flex: 1,
                padding: 'var(--spacing-md)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: submitMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
